import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import DashboardContext from '@/contexts/DashboardContext';
import { sensorMaintenanceLensService } from '../services/sensorMaintenanceLensService';
import type {
	SensorDevice,
	SensorMaintenanceResume,
	SensorMaintenanceState,
} from '../types/sensorMaintenanceLens.types';

const EMPTY_RESUME: SensorMaintenanceResume = {
	totalNumberOfDevices: 0,
	systemThroughput: '',
	active: 0,
	inactive: 0,
	withoutData: 0,
};

interface SensorMaintenanceLensContextValue {
	loading: boolean;
	resume: SensorMaintenanceResume;
	devices: SensorDevice[];
	visibleDevices: SensorDevice[];
	hiddenStates: Set<SensorMaintenanceState>;
	toggleStateVisibility: (state: SensorMaintenanceState) => void;
}

const SensorMaintenanceLensContext = createContext<SensorMaintenanceLensContextValue | undefined>(
	undefined,
);

export const useSensorMaintenanceLens = (): SensorMaintenanceLensContextValue => {
	const context = useContext(SensorMaintenanceLensContext);
	if (!context) {
		throw new Error('useSensorMaintenanceLens must be used within a SensorMaintenanceLensProvider');
	}
	return context;
};

function getOrganizationId(): string {
	try {
		return JSON.parse(localStorage.getItem('security') ?? '{}').organizationId ?? '';
	} catch {
		return '';
	}
}

export const SensorMaintenanceLensProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const { value: dashboardContext } = useContext(DashboardContext);
	const selectedLocation = dashboardContext.selectedLocation;
	const enabled = !!dashboardContext.activatedSensorMaintenanceMode;

	const [resume, setResume] = useState<SensorMaintenanceResume>(EMPTY_RESUME);
	const [devices, setDevices] = useState<SensorDevice[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [hiddenStates, setHiddenStates] = useState<Set<SensorMaintenanceState>>(new Set());

	useEffect(() => {
		if (!enabled || !selectedLocation) {
			setResume(EMPTY_RESUME);
			setDevices([]);
			return;
		}
		let cancelled = false;
		setLoading(true);
		Promise.all([
			sensorMaintenanceLensService.getResume({
				organizationId: getOrganizationId(),
				locationId: selectedLocation,
			}),
			sensorMaintenanceLensService.getDevices(selectedLocation),
		])
			.then(([resumeResult, devicesResult]) => {
				if (cancelled) return;
				setResume(resumeResult);
				setDevices(devicesResult);
			})
			.catch((error) => {
				if (cancelled) return;
				console.error('Error fetching sensor maintenance data:', error);
				setResume(EMPTY_RESUME);
				setDevices([]);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [enabled, selectedLocation]);

	useEffect(() => {
		setHiddenStates(new Set());
	}, [selectedLocation]);

	const visibleDevices = useMemo(
		() => devices.filter((device) => !hiddenStates.has(device.status)),
		[devices, hiddenStates],
	);

	const toggleStateVisibility = useCallback((state: SensorMaintenanceState) => {
		setHiddenStates((prev) => {
			const next = new Set(prev);
			if (next.has(state)) {
				next.delete(state);
			} else {
				next.add(state);
			}
			return next;
		});
	}, []);

	const contextValue = useMemo<SensorMaintenanceLensContextValue>(
		() => ({
			loading,
			resume,
			devices,
			visibleDevices,
			hiddenStates,
			toggleStateVisibility,
		}),
		[loading, resume, devices, visibleDevices, hiddenStates, toggleStateVisibility],
	);

	return (
		<SensorMaintenanceLensContext.Provider value={contextValue}>
			{children}
		</SensorMaintenanceLensContext.Provider>
	);
};
